import React, { useState, useRef } from "react";
import { Modal, Input, AutoComplete, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ReactComponent as NlpIcon } from '../../../../assets/icons/nlpSearch.svg';
import { searchProfessionsApi, nlSearchWriteWithAiApi } from '../../../../API/API';

const { TextArea } = Input;


const NlpSearchModal = ({ open, onClose, onSearch, hideTitleField = false, initialDescription = '', initialJobTitles = [] }) => {
  const [jobTitles, setJobTitles] = useState(initialJobTitles);
  const [searchText, setSearchText] = useState("");
  const [titleOpts, setTitleOpts] = useState([]);
  const [titleLoading, setTitleLoading] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  // Set once "Write with AI" is used, so the search runs against the plain
  // search endpoint instead of nl_search. Resets with the modal (destroyOnClose).
  const [aiGenerated, setAiGenerated] = useState(false);
  const [talentFilters, setTalentFilters] = useState(null);
  const titleTimer = useRef(null);
  const typewriterRef = useRef(null);

  const onTitleSearch = (val) => {
    setSearchText(val);
    if (titleTimer.current) clearTimeout(titleTimer.current);
    if (!val) { setTitleOpts([]); return; }
    titleTimer.current = setTimeout(async () => {
      setTitleLoading(true);
      try {
        const res = await searchProfessionsApi(val);
        const raw = res?.data?.data?.results ?? res?.data?.results ?? [];
        setTitleOpts(
          Array.isArray(raw)
            ? raw.filter(Boolean).map((item) => ({ label: item.title, value: item.title }))
            : []
        );
      } catch {
        setTitleOpts([]);
      } finally {
        setTitleLoading(false);
      }
    }, 400);
  };

  const addTitle = (val) => {
    const t = val?.trim();
    if (t && !jobTitles.includes(t)) setJobTitles((prev) => [...prev, t]);
    setSearchText("");
    setTitleOpts([]);
  };

  const removeTitle = (t) => setJobTitles((prev) => prev.filter((x) => x !== t));

  const handleSearch = () => {
    onSearch?.({ jobTitles, description, talentFilters, aiGenerated });
    onClose?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchText.trim()) {
      addTitle(searchText.trim());
    }
  };

  const handleWriteWithAi = async () => {
    setAiLoading(true);
    try {
      const res = await nlSearchWriteWithAiApi(jobTitles);
      const fullText = res?.data?.data?.generatedText;
      const filters = res?.data?.data?.talentFilters || null;
      setTalentFilters(filters);
      if (fullText) {
        setAiGenerated(true);
        setDescription("");
        setAiLoading(false);
        setAiTyping(true);
        if (typewriterRef.current) clearInterval(typewriterRef.current);
        let i = 0;
        typewriterRef.current = setInterval(() => {
          i++;
          setDescription(fullText.slice(0, i));
          if (i >= fullText.length) {
            clearInterval(typewriterRef.current);
            setAiTyping(false);
          }
        }, 18);
      }
    } catch {
      setAiLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={806}
      bodyStyle={{ padding: 0, borderRadius: 16 }}
      destroyOnClose
      title={null}
      style={{ top: 80 }}
      className="nlp-search-modal"
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px 24px 16px',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NlpIcon style={{ color: 'black', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, fontSize: 16, color: '#1B1F25' }}>Natural Language Search</span>
        </div>
        <span
          onClick={onClose}
          style={{ cursor: 'pointer', fontSize: 18, color: '#595959', lineHeight: 1 }}
        >
          ✕
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: '24px 24px 0' }}>
        {!hideTitleField && (
          <>
            <p style={{ color: 'var(--Color-Theme-Dark, #011D28)', fontSize: 13, marginBottom: 14 }}>
              Filter to current job titles so that we focus on the best candidates.
            </p>
            <AutoComplete
              options={titleOpts}
              value={searchText}
              onSearch={onTitleSearch}
              onSelect={addTitle}
              style={{ width: '100%' }}
              notFoundContent={titleLoading ? "Searching…" : null}
            >
              <Input
                placeholder="e.g. Software Engineer"
                onKeyDown={handleKeyDown}
                allowClear
                style={{ height: 40, borderRadius: 4, padding: '8px 12px' }}
              />
            </AutoComplete>
          </>
        )}

        {!hideTitleField && jobTitles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, marginBottom: 16 }}>
            {jobTitles.map((t) => (
              <Tag
                key={t}
                closable
                onClose={() => removeTitle(t)}
                style={{
                  borderRadius: 4,
                  border: '1px solid var(--Color-State-Important, #1E90FF)',
                  color: 'var(--Color-Theme-Primary, #002D40)',
                  background: 'var(--Color-Background-Card-Important, #ECF5FF)',
                  padding: '2px 10px',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {t}
              </Tag>
            ))}
          </div>
        )}

        <p style={{ color: 'var(--Color-Theme-Primary, #002D40)', fontSize: 13, fontWeight: 500, marginBottom: 10, marginTop: 16 }}>
          Then describe ideal candidates in as much detail as possible so SkillGigs can source top matches.
        </p>

        <div style={{ position: 'relative', marginBottom: 0 }}>
          {aiLoading ? (
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: '10px 12px', minHeight: 200 }}>
              <div className="nlp-skeleton">
                <div className="nlp-skeleton-line" />
                <div className="nlp-skeleton-line nlp-skeleton-line-mid" />
                <div className="nlp-skeleton-line nlp-skeleton-line-short" />
              </div>
            </div>
          ) : (
            <TextArea
              rows={8}
              placeholder={`e.g. Experience in Fintech or SaaS startups\nHas mentored Junior Engineers and is an expert at refactoring code\nCommunicates well with C-suite executives`}
              value={description}
              readOnly={aiTyping}
              onChange={(e) => { if (!aiTyping) setDescription(e.target.value); }}
              style={{
                resize: 'none',
                borderRadius: 4,
                paddingTop: 10,
                color: aiTyping ? '#98a2b3' : description ? '#101828' : undefined,
                transition: 'color 0.5s ease',
              }}
            />
          )}
          {!description.trim() && !aiLoading && <button
            type="button"
            disabled={jobTitles.length === 0}
            onClick={handleWriteWithAi}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              background: 'var(--Color-Background-Card-Important, #ECF5FF)',
              border: '1px solid #d0d5dd',
              borderRadius: 8,
              padding: '5px 12px',
              fontSize: 13,
              fontFamily: 'Montserrat, sans-serif',
              color: '#344054',
              cursor: jobTitles.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontWeight: 500,
              opacity: jobTitles.length === 0 ? 0.45 : 1,
              whiteSpace: 'nowrap',
              transition: 'border-color 0.2s',
            }}
          >
            ✦ Write with AI
          </button>}
        </div>
      </div>

      {/* Footer — Search button */}
      <div style={{ padding: '24px', borderTop: '1px solid #f0f0f0' }}>
        <button
          type="button"
          onClick={handleSearch}
          disabled={(!hideTitleField && jobTitles.length === 0) || aiLoading || aiTyping}
          style={{
            width: '100%',
            background: description.trim() ? 'var(--Color-Theme-Primary, #002D40)' : 'var(--opacity-black-5, #0000000D)',
            border: 'none',
            borderRadius: 4,
            fontSize: 15,
            color: description.trim() ? '#fff' : '#1B1F25',
            cursor: ((!hideTitleField && jobTitles.length === 0) || aiLoading || aiTyping) ? 'not-allowed' : 'pointer',
            opacity: ((!hideTitleField && jobTitles.length === 0) || aiLoading || aiTyping) ? 0.45 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '10px 0',
          }}
        >
          Search <SearchOutlined />
        </button>
      </div>
    </Modal>
  );
};

export default NlpSearchModal;
